<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Button, ButtonV2, Checkbox, Input, InputV2, Toggle, ToggleV2, toast } from '@Shelly/shelly-design-system';
	//Hooks
	import { onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { localReq, postReq } from '../../../../services/http';
	//Constants
	import { DEVICE_MAX_POWER_DEFAULTS } from '../../../../constants';
	//Helpers
	import {
		getErrorOrInfoMessageForMinAndMax,
	} from '../../../../helpers/utilities';
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	import { capFirstChar } from '../../../../helpers/utilities';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import SettingGuide from '../../SettingGuide.svelte';
	import ResetToDefaultLink from '../../ResetToDefaultLink.svelte';

	export let device;

	const id = real_id(device.id);

	let settings = readDeviceSettings(id);

	const min = 0;

	const max = DEVICE_MAX_POWER_DEFAULTS[device.type];

	let value, checked, cacheValue, cacheChecked;
	let lastCustomValue = 0;
	let loading = false;

	let local_ip = get_local_ip(id);
	let translationsWithWatts = true;

	$: inputData = getErrorOrInfoMessageForMinAndMax(value, min, max, translationsWithWatts);

	onMount(() => {
		if (device.category !== 'emeter' && device.category !== 'roller') {
			value = settings[device.category + 's'][device.channel].max_power;
		} else if (device.category === 'emeter') {
			value = settings[device.category + 's'][device.channel].max_power;
		}
		checked = value > 0;
		cacheChecked = checked;
		cacheValue = value;
		lastCustomValue = checked ? value : max;
	});

	const savePower = async () => {
		if (cacheChecked !== checked || cacheValue !== value) {
			let res;
			loading = true;
			if (local_ip) {
				res = await localReq(device, `${local_ip}/settings`, 'post', {
					max_power: value,
				});
			} else {
				res = await postReq(
					`/device/${device.category !== 'emeter' ? device.category : 'relay'}/settings/power_max`,
					{
						value: value,
						channel: device.channel,
						id,
					},
				);
			}
			if (res.isok) {
				settings[device.category + 's'][device.channel].max_power = value;
				devices_settings.update_devices_settings(device.id, settings);
				checked = +value > 0;
				if (+value > 0) {
					lastCustomValue = value;
				}
				if (value === 0) {
					toast.open({
						type: 'success',
						title: $format('power_protection_disable_custom', {
							default: `${capFirstChar(device.type)} custom power protection was disabled.`,
						}),
					});
				} else {
					toast.open({
						type: 'success',
						title: $format('power_protection_save_scc_toast', {
							values: { value: value },
							default: `${capFirstChar(device.type)} custom power protection was set to ${value}W.`,
						}),
					});
				}
				cacheChecked = checked;
				cacheValue = value;
			} else {
				checked = cacheChecked;
				value = cacheValue;
				toast.open({
					type: 'danger',
					title: $format('power_protection_save_err_toast'),
				});
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			loading = false;
		}
	};
	const handleToggler = async () => {
		if (loading) return;

		checked = !checked;
		if (checked) {
			value = +lastCustomValue > 0 ? lastCustomValue : max;
		} else {
			if (+value > 0) {
				lastCustomValue = value;
			}
			value = 0;
		}

		if ($showNewLayout) {
			await savePower();
		}
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout && checked) {
			setStickyBottomAction({
				onClick: savePower,
				loading,
				disabled: loading,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	$: InputC = $showNewLayout ? InputV2 : Input;
	$: ButtonC = $showNewLayout ? ButtonV2 : Button;
	$: ToggleC = $showNewLayout ? ToggleV2 : Toggle;

	const resetToDefault = () => {
		value = max;
	};
</script>

<SettingGuide
	text={$format('max_power_prot_description', {
		values: { value: checked ? value : max },
		default: `Device will turn off when power reaches ${checked ? value : max} W.`,
	})}
/>

{#if $showNewLayout}
	<svelte:component
		this={ToggleC}
		size="m"
		class="mt-s-plus mb-s-plus"
		{checked}
		disabled={loading}
		on:click={handleToggler}
		id="toggle-c3v0n4x"
	>
		{$format('custom_max_power_protection', { default: 'Custom max power protection' })}
	</svelte:component>
{:else}
	<Checkbox size="m" class="mb-s" bind:checked on:click={handleToggler} id="checkbox-x2i6omj">
		{$format('custom_max_power_protection', { default: 'Custom max power protection' })}
	</Checkbox>
{/if}
{#if checked}
	<svelte:component
		this={InputC}
		aria-label={$format('input_power_protection_max_power', {
			default: 'Input Power Protection Max Power',
		})}
		on:input={(e) => restrictDecimalNumberInput(e, 2)}
		size="m"
		bind:value
		type="number"
		{min}
		{max}
		step={1}
		info={inputData.info}
		error={inputData.error}
		disabled={!checked}
		id="input-rbt37ee"
	/>
	<ResetToDefaultLink disabled={+value === +max} on:click={resetToDefault} />
	{#if !$showNewLayout}
		<svelte:component
			this={ButtonC}
			class="mt-s"
			{loading}
			disabled={loading}
			size="m"
			width="full"
			on:click={savePower}
		>
			{$format('button_save', { default: 'Save' })}
		</svelte:component>
	{/if}
{/if}
